import React from "react";
import * as Yup from "yup";
import { useFormik } from "formik";
import { TextField, Toolbar } from "@material-ui/core";
import Grid from "@material-ui/core/Grid";
import { PrimaryButton, SecondaryButton } from "@prestosports/commons-ui";
import Box from "@material-ui/core/Box";
import Autocomplete from "@material-ui/lab/Autocomplete";
import addPlayerStyles from "../../common/styles/sports/AddPlayer.styles";
import commonStyles from "../../common/styles/Common.styles";
import { usePlayersContext } from "../../../state/players/context";
import { useSportContext } from "../../../state/sport/context";

const AddPlayer = ({ team, player, onSave, onCancel }) => {
	const commonClasses = commonStyles();
	const classes = addPlayerStyles();

	const { state, savePlayer } = usePlayersContext();
	const players = state.players(team.id);
	let playerUniforms = players.map((p) => p.uniform);
	if (player) {
		playerUniforms = playerUniforms.filter((pu) => pu !== player?.uniform);
	}

	const { state: sportState } = useSportContext();
	const positions = sportState.allowedPositions();
	const uniformLength = 3;

	const validationSchema = Yup.object({
		firstName: Yup.string().required("First name is required"),
		lastName: Yup.string().required("Last name is required"),
		uniform: Yup.string()
			.matches(
				/^[A-Za-z0-9]{1,3}$/,
				`Number must contain up to ${uniformLength} alpha characters`
			)
			.notOneOf(playerUniforms, "Number already exists")
			.required("Number is required"),
		primaryPosition: Yup.object()
			.nullable()
			.when(["secondaryPosition"], (secondaryPosition, schema) => {
				return schema.test({
					test: (primaryPosition) => {
						if (primaryPosition && secondaryPosition)
							return primaryPosition?.name !== secondaryPosition?.name;
						return true;
					},
					message: "Primary and Secondary positions cannot be the same"
				});
			})
	});

	const formik = useFormik({
		initialValues: {
			firstName: "",
			lastName: "",
			uniform: "",
			primaryPosition: player?.positions?.[0],
			secondaryPosition: player?.positions?.[1],
			...player
		},
		validationSchema,
		onSubmit: async (values) => {
			const { primaryPosition, secondaryPosition, ...playerObj } = values;
			await savePlayer({
				...playerObj,
				positions: [primaryPosition, secondaryPosition].filter((p) => p),
				...{ teamId: team.id }
			});

			if (onSave) {
				onSave();
			}
		}
	});

	const {
		errors,
		touched,
		handleSubmit,
		handleChange,
		handleBlur,
		isValid,
		dirty,
		setFieldValue,
		setTouched
	} = formik;

	const cancel = () => {
		if (onCancel) {
			onCancel();
		}
	};
	return (
		<Box className={commonClasses.container}>
			<Box className={classes.contentContainer}>
				<form className={commonClasses.form} onSubmit={handleSubmit}>
					<Grid container alignItems="center" className={classes.detailsContainer}>
						<Grid
							container
							spacing={3}
							direction="column"
							item
							xs={12}
							sm={12}
							md={4}
						>
							<Grid item>
								<TextField
									id="uniform"
									name="uniform"
									label="Number"
									fullWidth
									helperText={touched.uniform ? errors.uniform : ""}
									error={touched.uniform && Boolean(errors.uniform)}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formik.values.uniform}
								/>
							</Grid>
							<Grid item>
								<TextField
									id="firstName"
									name="firstName"
									label="First name"
									fullWidth
									helperText={touched.firstName ? errors.firstName : ""}
									error={touched.firstName && Boolean(errors.firstName)}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formik.values.firstName}
								/>
							</Grid>
							<Grid item>
								<TextField
									id="lastName"
									name="lastName"
									label="Last name"
									fullWidth
									helperText={touched.lastName ? errors.lastName : ""}
									error={touched.lastName && Boolean(errors.lastName)}
									onChange={handleChange}
									onBlur={handleBlur}
									value={formik.values.lastName}
								/>
							</Grid>

							<Grid item>
								<Autocomplete
									id="primaryPosition"
									options={positions}
									getOptionLabel={(option) => `${option.name} (${option.code})`}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Primary Position"
											variant="outlined"
											helperText={
												touched.primaryPosition || touched.secondaryPosition
													? errors.primaryPosition
													: ""
											}
											error={
												(touched.primaryPosition || touched.secondaryPosition) &&
												Boolean(errors.primaryPosition)
											}
										/>
									)}
									onChange={(event, value) => {
										setTouched({ primaryPosition: true }, false);
										setFieldValue("primaryPosition", value);
									}}
									value={formik.values.primaryPosition}
								/>
							</Grid>

							<Grid item>
								<Autocomplete
									id="secondaryPosition"
									options={positions}
									getOptionLabel={(option) => `${option.name} (${option.code})`}
									renderInput={(params) => (
										<TextField
											{...params}
											label="Secondary Position"
											variant="outlined"
										/>
									)}
									onChange={(event, value) => {
										setTouched({ secondaryPosition: true }, false);
										setFieldValue("secondaryPosition", value);
									}}
									value={formik.values.secondaryPosition}
								/>
							</Grid>
						</Grid>
					</Grid>

					<Toolbar className={commonClasses.actionToolbar}>
						<Grid container direction="row" justifyContent="flex-end" spacing={2}>
							<Grid item>
								<SecondaryButton color="secondary" label="Cancel" onClick={cancel} />
							</Grid>
							<Grid item>
								<PrimaryButton
									type="submit"
									color="primary"
									label="Save"
									disabled={!(isValid && dirty)}
								/>
							</Grid>
						</Grid>
					</Toolbar>
				</form>
			</Box>
		</Box>
	);
};

export default AddPlayer;
